/**
 * Warm lazy route chunks on nav intent (hover/focus/touch).
 * Deduped — safe to call often from sidebar.
 *
 * Idle warming is staggered (one chunk per idle slice) so navigation
 * does not compete with a burst of large dynamic imports.
 */

import { peekRoleType } from '../infrastructure/startup/bootSurface';
import { getFirstAllowedAppPath, MANAGER_IDLE_PREFETCH_PATHS } from '../routing/appRouteCatalog';
import { getBusinessTypeId } from './restaurantMode';
import { hasPagePermission } from './workerPermissions';

function resolveHomeLoader(roleType, businessType) {
  const typeId = getBusinessTypeId(businessType);
  if (typeId === 'pharmacie' && (roleType === 'manager' || roleType === 'admin')) {
    return () => import('../pages/PharmacyDashboard');
  }
  if (roleType === 'manager') {
    return () => import('../components/dashboard/NewManagerDashboard');
  }
  return () => import('../pages/Dashboard');
}

function homeCacheKey(roleType, businessType) {
  const typeId = getBusinessTypeId(businessType) || '';
  const role = roleType || 'pos';
  return `/#${role}:${typeId}`;
}

const PREFETCH_BY_PATH = {
  '/inventory': () => import('../components/ConditionalInventory'),
  '/stock': () => import('../components/ConditionalStock'),
  '/sales': () => import('../pages/Sales'),
  '/purchases': () => import('../pages/Purchases'),
  '/orders': () => import('../pages/Orders'),
  '/suppliers': () => import('../pages/Suppliers'),
  '/customers': () => import('../pages/Customers'),
  '/reports': () => import('../pages/Reports'),
  '/payments': () => import('../pages/PaymentManagement'),
  // Heavy EnhancedCashReportModal — never idle-warm; sidebar hover may still intent-prefetch
  '/payment-report': () => import('../pages/PaymentReport'),
  '/expenses': () => import('../pages/Expenses'),
  '/restaurant': () => import('../pages/Restaurant'),
  '/settings': () => import('../pages/Settings'),
  '/mitarbeiter': () => import('../pages/EmployeesPage'),
};

const warmed = new Set();

/**
 * @param {string} path
 * @param {{ roleType?: string|null, businessType?: unknown }} [options]
 *        For `/`, pass the known role so Login's POS warm does not block Manager/Pharmacy.
 */
export function prefetchNavRoute(path, options = {}) {
  if (!path) return;
  // Never background-warm payment-report — it pulls EnhancedCashReportModal + heavy CSS
  // and stays in memory for the whole session (feels like InstantCache "persisting").
  if (path === '/payment-report' || path.startsWith('/payment-report/')) return;

  let cacheKey = path;
  let loader = PREFETCH_BY_PATH[path];

  if (path === '/') {
    const roleType = options.roleType ?? peekRoleType();
    const businessType = options.businessType;
    cacheKey = homeCacheKey(roleType, businessType);
    loader = resolveHomeLoader(roleType, businessType);
  }

  if (!loader || warmed.has(cacheKey)) return;
  warmed.add(cacheKey);
  void loader().catch(() => {
    warmed.delete(cacheKey);
  });
}

/** Clear intent-warmed path markers (logout / business switch). Chunks stay in module cache. */
export function resetNavRoutePrefetchMemory() {
  warmed.clear();
}

/**
 * Warm ConditionalRoutes (thin) + best-effort landing guess before PIN.
 * Real role warm happens in `warmLandingForRole` after CodeEntry.
 */
export function warmShellRouteChunks() {
  void import('../components/ConditionalRoutes.jsx').catch(() => {});
  prefetchNavRoute('/');
  prefetchNavRoute('/sales');
  const role = peekRoleType();
  if (role === 'manager') {
    prefetchNavRoute('/inventory');
    prefetchNavRoute('/stock');
  }
}

/**
 * Call once the role is known (PIN / cash confirm) — warms the exact landing chunk
 * before `navigate()`, so Suspense often never paints.
 */
export function warmLandingForRole({ role, businessType, path } = {}) {
  if (!role) return;
  const target =
    path ||
    getFirstAllowedAppPath({
      businessType,
      hasPermission: (permission) => hasPagePermission(role, permission),
    });
  prefetchNavRoute(target, {
    roleType: role.type,
    businessType,
  });
  if (role.type === 'manager' || role.type === 'admin') {
    prefetchNavRoute('/inventory');
    prefetchNavRoute('/stock');
    prefetchNavRoute('/reports');
  }
}

/** After shell is idle, warm destinations one-at-a-time (low priority).
 *  Prefetch keeps startTransition navigations near-instant; stagger so
 *  chunk parse does not stall the main thread on tablets. */
export function scheduleIdleNavPrefetch(
  paths = ['/reports', '/sales', '/purchases', '/customers', '/expenses', '/settings', '/suppliers', '/orders']
) {
  if (typeof window === 'undefined') return () => {};

  let cancelled = false;
  let idleId = null;
  let timerId = null;
  let index = 0;
  const list = (Array.isArray(paths) ? paths : MANAGER_IDLE_PREFETCH_PATHS).filter(Boolean);

  const scheduleNext = () => {
    if (cancelled || index >= list.length) return;
    const run = (deadline) => {
      if (cancelled) return;
      // Prefer real idle time; still allow one warm if timeout fired.
      const hasTime =
        !deadline ||
        typeof deadline.timeRemaining !== 'function' ||
        deadline.timeRemaining() > 10 ||
        deadline.didTimeout;
      if (!hasTime) {
        idleId = window.requestIdleCallback(run, { timeout: 3000 });
        return;
      }
      prefetchNavRoute(list[index]);
      index += 1;
      if (index < list.length) {
        timerId = window.setTimeout(() => {
          timerId = null;
          if (typeof window.requestIdleCallback === 'function') {
            idleId = window.requestIdleCallback(run, { timeout: 3000 });
          } else {
            scheduleNext();
          }
        }, 220);
      }
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      timerId = window.setTimeout(() => run(null), 700);
    }
  };

  scheduleNext();

  return () => {
    cancelled = true;
    if (idleId != null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId);
    }
    if (timerId != null) window.clearTimeout(timerId);
  };
}
